import { LineChart } from 'lucide-react';
import { useSessionTrends } from '@/hooks/useSessionTrends';
import styles from '../DashboardPage.module.css';

/**
 * Session trends widget. Two headline averages (last 7d) + two
 * stacked auto-scaled sparklines showing 30 daily buckets.
 *
 * Stacked over overlaid: duration (15-90 min) and kWh (5-50)
 * have meaningfully different scales. A single combined chart
 * either picks one scale and squashes the other, or normalizes
 * both to 0..1 and hides the actual numbers. Two separate
 * sparklines, each auto-scaled to its own metric's min..max,
 * shows the real shape of each trend without distortion.
 *
 * Null buckets (days with zero completed sessions) are skipped
 * in the polyline — the line draws across the gap, longer
 * gaps stretch visibly. Better than carrying-forward (lies
 * about that day's activity) or substituting zero (looks like
 * a crash).
 */
export default function SessionTrendsWidget() {
  const q = useSessionTrends();
  const data = q.data;

  return (
    <div className={styles.chartBody}>
      <div className={styles.trendsHeader}>
        <div className={styles.chartIcon}><LineChart size={14} /></div>
        <p className={styles.chartTitle}>Session trends</p>
      </div>

      {q.isLoading && !data && (
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartDesc}>Loading…</p>
        </div>
      )}

      {data && data.avg_duration_minutes_7d === null && data.avg_kwh_7d === null && (
        <div className={styles.anomEmpty}>
          <span className={styles.anomEmptyIcon}><LineChart size={18} /></span>
          <p>
            {data.is_scoped
              ? 'No completed sessions at your assigned locations in the last 30 days.'
              : 'No completed sessions in the last 30 days.'}
          </p>
        </div>
      )}

      {data && (data.avg_duration_minutes_7d !== null || data.avg_kwh_7d !== null) && (
        <>
          <div className={styles.trendsStatsRow}>
            <div className={styles.trendsStat}>
              <span className={styles.trendsStatLabel}>Avg duration · 7d</span>
              <span className={styles.trendsStatValue}>
                {data.avg_duration_minutes_7d !== null
                  ? Math.round(data.avg_duration_minutes_7d)
                  : '—'}
                <span className={styles.trendsStatUnit}>min</span>
              </span>
            </div>
            <div className={styles.trendsStat}>
              <span className={styles.trendsStatLabel}>Avg energy · 7d</span>
              <span className={styles.trendsStatValue}>
                {data.avg_kwh_7d !== null ? data.avg_kwh_7d.toFixed(1) : '—'}
                <span className={styles.trendsStatUnit}>kWh</span>
              </span>
            </div>
          </div>

          <div className={styles.trendsSparklineBlock}>
            <span className={styles.trendsSparklineLabel}>
              <span className={`${styles.trendsSparklineDot} ${styles.trendsSparklineDotDuration}`} />
              Duration · 30d
            </span>
            <AutoScaleSparkline
              values={data.daily_buckets.map((b) => b.avg_duration_minutes)}
              fillClassName={styles.trendsSparklineFillDuration ?? ''}
              strokeClassName={styles.trendsSparklineStrokeDuration ?? ''}
            />
          </div>

          <div className={styles.trendsSparklineBlock}>
            <span className={styles.trendsSparklineLabel}>
              <span className={`${styles.trendsSparklineDot} ${styles.trendsSparklineDotEnergy}`} />
              Energy · 30d
            </span>
            <AutoScaleSparkline
              values={data.daily_buckets.map((b) => b.avg_kwh)}
              fillClassName={styles.trendsSparklineFillEnergy ?? ''}
              strokeClassName={styles.trendsSparklineStrokeEnergy ?? ''}
            />
          </div>

          <div className={styles.trendsCaption}>
            <span>30d ago</span>
            <span>today</span>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Auto-scale sparkline. Distinct from the ChargerUptimeWidget's
 * `Sparkline` because that one locks y-domain to 0..100 (uptime
 * is always a percent), whereas trend metrics need per-window
 * auto-scaling so the shape reads regardless of the metric's
 * natural range (15-90 min for duration, 5-50 for kWh, etc.).
 *
 * Null values are skipped — the polyline emits points only for
 * non-null entries, so a long no-activity stretch shows as a
 * visible line across the gap rather than collapsing to zero.
 *
 * Edge cases:
 *   - All values null   → no points → renders empty SVG (caller
 *                         already skips the widget body in this
 *                         case, but defensive)
 *   - All values equal  → range = 0, every point at mid-height
 *                         (avoids divide-by-zero)
 *   - Single non-null   → renders a single dot at mid-height
 */
function AutoScaleSparkline({
  values, fillClassName, strokeClassName,
}: {
  values: Array<number | null>;
  fillClassName: string;
  strokeClassName: string;
}) {
  const W = 100;
  const H = 100;

  // Filter to indexed pairs of non-null values so the x-axis
  // skips gaps cleanly. Each surviving point keeps its original
  // index so the line stretches across nulls visually.
  const pts: Array<readonly [number, number]> = [];
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (v === null || v === undefined || !Number.isFinite(v)) continue;
    pts.push([i, v]);
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (pts.length === 0) {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={styles.trendsSparkline} />
    );
  }
  const range = hi - lo;
  const step = values.length > 1 ? W / (values.length - 1) : W;

  const linePoints = pts.map(([i, v]) => {
    const x = i * step;
    // Pad 5% top + 5% bottom so the stroke isn't flush against
    // the SVG edge. Without padding a max-value point gets
    // clipped at y=0.
    const norm = range === 0 ? 0.5 : (v - lo) / range;
    const y = H - (5 + norm * 90);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');

  // Fill: line points → bottom-right of the LAST point's x →
  // bottom-left of the FIRST point's x. Anchored to the line's
  // x-extent so the fill respects gaps at the start/end too.
  const firstX = (pts[0]![0] * step).toFixed(2);
  const lastX = (pts[pts.length - 1]![0] * step).toFixed(2);
  const fillPoints = `${linePoints} ${lastX},${H} ${firstX},${H}`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={styles.trendsSparkline}
      role="img"
      aria-label="30-day trend sparkline"
    >
      <polygon className={fillClassName} points={fillPoints} />
      <polyline className={strokeClassName} points={linePoints} />
    </svg>
  );
}
